import { useNavigate, useLocation } from 'react-router-dom';
import type { Conversation } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';

/**
 * Opens a channel, joining it first if you're not in it (public channels only; the API refuses private ones).
 * After a join, a toast offers "Leave", which leaves again and takes you back to where you were.
 */
export function useOpenChannel() {
  const ws = useWorkspaceId();
  const nav = useNavigate();
  const loc = useLocation();
  const toast = useUi(s => s.toast);
  return async (c: Conversation, member: boolean) => {
    if (member) { nav(`/c/${c.id}`); return true; }
    const back = loc.pathname.startsWith(`/c/${c.id}`) ? '/chat' : `${loc.pathname}${loc.search}`;
    try {
      await api.joinChannel(c.id);
      await queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') });
      void queryClient.invalidateQueries({ queryKey: ['browse', ws] });
      nav(`/c/${c.id}`);
      toast(`Joined #${c.slug}`, 'success', {
        label: 'Leave',
        run: () => void api.leaveConversation(c.id)
          .then(() => queryClient.invalidateQueries({ queryKey: qk.conversations(ws ?? '') }))
          .then(() => { void queryClient.invalidateQueries({ queryKey: ['browse', ws] }); nav(back); toast(`Left #${c.slug}`); })
          .catch(e => toast(errorMessage(e), 'error')),
      });
      return true;
    } catch (e) { toast(errorMessage(e), 'error'); return false; }
  };
}
